import React, { useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import { getAppVersions } from '@/redux/actions/gravityActions';
import pushChangesQueueItem from '@/redux/actions/pushChangesQueueItem';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import SnapshotsDrawer from './SnapshotsDrawer';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';

const AppSnapshotsProvider = ({ drawerKey = 'appSnapshots' }) => {
  const appId = useSelector((state) => appScreenSelectors.activeApp(state)?.id);
  const isDraft = useSelector((state) => appScreenSelectors.activeApp(state)?.version === 'draft');
  const drawerState = useSelector((state) => state?.ui?.drawers?.[drawerKey]);

  const appVersions = useSelector(memoizeSelector('appSnapshotsProvider:appVersions', (state) => state.workflow.stepAppVersions[appId]));

  useEffect(() => {
    if (drawerState?.open && appId && !appVersions) {
      getAppVersions({ appId });
    }
  }, [drawerState?.open, appId, appVersions]);

  const processedAppVersions = useMemo(() => {
    if (!appVersions) return [];

    const processed = appVersions
      .filter((version) => version.version !== '0') // Exclude version "0"
      .map((version, index) => ({
        id: `${appId}-${version.version}-${index}`, // Unique identifier with index
        version: version.version,
        createdOn: version.publishDate || version.publish_date,
        createdBy: version.createdBy || version.created_by,
        description: version.notes || '',
        isPublished: version.isPublished || version.is_published,
        stepCount: version.stepCount || version.step_count || 0,
        snapshotStatus: 'healthy' // Apps don't have health status like workflows
      }));

    return processed;
  }, [appVersions, appId]);

  const setAppVersion = useCallback((appId, appVersion, path, value) => {
    return new Promise((resolve) => {
      pushChangesQueueItem('app', { appId, appVersion, path, value });
      resolve();
    });
  }, []);

  const handleUpdate = useCallback(
    (snapshot, updatedData) => {
      return new Promise((resolve, reject) => {
        try {
          setAppVersion(appId, snapshot.version, 'notes', updatedData.description);
          getAppVersions({ appId });
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
    [appId, setAppVersion]
  );

  const handleSeeChanges = useCallback((snapshot) => {
    setModalState('diffs', {
      open: true,
      type: 'app',
      oldVersion: snapshot.version,
      currentVersion: 'draft'
    });
  }, []);

  const customActions = useMemo(
    () => ({
      onUpdate: handleUpdate,
      onSeeChanges: handleSeeChanges
    }),
    [handleUpdate, handleSeeChanges]
  );

  return <SnapshotsDrawer drawerKey={drawerKey} title="App Versions" snapshots={processedAppVersions} isDraft={isDraft} customActions={customActions} className="snapshots-drawer app-snapshots-drawer" />;
};

export default AppSnapshotsProvider;
